import { useState } from 'react'
import { Clock, Trash2, X } from 'lucide-react'
import { useNavigate } from 'react-router-dom'
import { Button } from '@/components/ui/button'
import { NodeListPage, BaseNodeFiltersState } from '@/components/nodes/NodeListPage'
import { useHistory } from '@/hooks/useHistory'
import { PageHistoryItem } from '@/contexts/HistoryContext.context'
import { cn } from '@/lib/utils/cn'
import { BaseFiltersContainer } from '@/components/queries/BaseFiltersContainer'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import type { FilterControlConfig, FilterOption } from '@/types/filters'
import type { LucideIcon } from 'lucide-react'

// Define node types for history filtering and grouping
type HistorynodeType = 'workspace' | 'project' | 'task' | 'message' | 'document' | 'user' | 'other'

// Define the filters interface for history
export interface HistoryFilters extends BaseNodeFiltersState {
  nodeType: string // 'all' or HistorynodeType value
}

// Define the grouping options for history
export type HistoryGroupBy = 'none' | 'nodeType'

// Extract node type from history path
function extractnodeType(path: string): HistorynodeType {
  const pathSegments = path.split('/').filter(Boolean)

  if (path === '/' || pathSegments.length === 0) return 'other'

  const firstSegment = pathSegments[0]

  switch (firstSegment) {
    case 'workspaces':
      return 'workspace'
    case 'projects':
      return 'project'
    case 'tasks':
      return 'task'
    case 'messages':
      return 'message'
    case 'documents':
      return 'document'
    case 'users':
      return 'user'
    case 'reports':
    case 'inbox':
    case 'search':
    case 'onboarding':
      return 'other'
    default:
      return 'other'
  }
}

// History filters component
function HistoryFilters({
  filters,
  onFiltersChange,
  className,
  collapsed = false,
}: {
  filters: HistoryFilters
  onFiltersChange: (filters: Partial<HistoryFilters>) => void
  className?: string
  collapsed?: boolean
}) {
  const historyFilterConfigs: FilterControlConfig<HistoryFilters>[] = [
    {
      key: 'nodeType',
      label: 'Node Type',
      options: [
        { value: 'all', label: 'All Types' },
        { value: 'workspace', label: 'Workspaces' },
        { value: 'project', label: 'Projects' },
        { value: 'task', label: 'Tasks' },
        { value: 'message', label: 'Messages' },
        { value: 'document', label: 'Documents' },
        { value: 'user', label: 'Users' },
        { value: 'other', label: 'Other' },
      ] as FilterOption[],
    },
  ]

  return (
    <BaseFiltersContainer
      filters={filters}
      onFiltersChange={onFiltersChange}
      filterConfigs={historyFilterConfigs}
      className={className}
      collapsed={collapsed}
      gridCols="grid-cols-1 md:grid-cols-2 lg:grid-cols-4"
    />
  )
}

// History grouping selector component
function HistoryGroupingSelector({
  value,
  onChange,
  className,
}: {
  value: HistoryGroupBy
  onChange: (value: HistoryGroupBy) => void
  className?: string
}) {
  return (
    <Select value={value} onValueChange={onChange}>
      <SelectTrigger className={className}>
        <SelectValue placeholder="Group by" />
      </SelectTrigger>
      <SelectContent>
        <SelectItem value="none">No Grouping</SelectItem>
        <SelectItem value="nodeType">Group by Node Type</SelectItem>
      </SelectContent>
    </Select>
  )
}

// Helper function to safely check if icon is a valid React component
function isValidIconComponent(icon: unknown): icon is LucideIcon {
  return (
    typeof icon === 'function' || (typeof icon === 'object' && icon !== null && '$$typeof' in icon)
  )
}

// Helper component to safely render icon
function SafeIcon({ icon, className }: { icon?: LucideIcon | unknown; className?: string }) {
  if (!icon) return null

  // Type guard to ensure icon is a valid React component
  if (!isValidIconComponent(icon)) {
    console.warn('Invalid icon detected in history item, skipping render')
    return null
  }

  const IconComponent = icon as LucideIcon
  return <IconComponent className={className} />
}

// History results grouping component
function HistoryResultsGrouping({
  nodes,
  groupBy,
  className,
  onRemoveFromHistory,
}: {
  nodes?: PageHistoryItem[]
  groupBy: HistoryGroupBy
  className?: string
  onRemoveFromHistory?: (e: React.MouseEvent, id: string) => void
}) {
  const results = nodes || []
  const navigate = useNavigate()

  if (results.length === 0) {
    return null
  }

  // Helper function to get node type display name
  const getnodeTypeDisplayName = (nodeType: HistorynodeType): string => {
    switch (nodeType) {
      case 'workspace':
        return 'Workspaces'
      case 'project':
        return 'Projects'
      case 'task':
        return 'Tasks'
      case 'message':
        return 'Messages'
      case 'document':
        return 'Documents'
      case 'user':
        return 'Users'
      case 'other':
        return 'Other'
      default:
        return 'Unknown'
    }
  }

  if (groupBy === 'none') {
    return (
      <div className={cn('space-y-3', className)}>
        {results.map((item) => (
          <div
            key={item.id}
            className="flex items-center gap-3 p-3 rounded-lg border hover:bg-accent hover:text-accent-foreground cursor-pointer group"
            onClick={() => navigate(item.path)}
          >
            <div className="flex items-center gap-3 flex-1 min-w-0">
              <SafeIcon icon={item.icon} className="h-5 w-5 text-muted-foreground flex-shrink-0" />
              <div className="flex-1 min-w-0">
                <div className="font-medium text-base truncate">{item.title}</div>
                <div className="text-sm text-muted-foreground truncate">{item.path}</div>
                <div className="text-xs text-muted-foreground mt-1">
                  {(() => {
                    const date = new Date(item.timestamp)
                    const now = new Date()
                    const diffInMinutes = Math.floor((now.getTime() - date.getTime()) / (1000 * 60))

                    if (diffInMinutes < 1) return 'Just now'
                    if (diffInMinutes < 60) return `${diffInMinutes}m ago`

                    const diffInHours = Math.floor(diffInMinutes / 60)
                    if (diffInHours < 24) return `${diffInHours}h ago`

                    const diffInDays = Math.floor(diffInHours / 24)
                    if (diffInDays < 7) return `${diffInDays}d ago`

                    return date.toLocaleDateString()
                  })()}
                </div>
              </div>
            </div>
            {onRemoveFromHistory && (
              <Button
                variant="ghost"
                size="sm"
                onClick={(e) => onRemoveFromHistory(e, item.id)}
                className="h-auto p-1 text-muted-foreground hover:text-destructive opacity-0 group-hover:opacity-100 transition-opacity flex-shrink-0"
                title="Remove from history"
              >
                <X className="h-4 w-4" />
              </Button>
            )}
          </div>
        ))}
      </div>
    )
  }

  if (groupBy === 'nodeType') {
    // Group items by node type
    const groupedItems = results.reduce(
      (groups, item) => {
        const nodeType = extractnodeType(item.path)
        const key = nodeType

        if (!groups[key]) {
          groups[key] = []
        }
        groups[key].push(item)
        return groups
      },
      {} as Record<string, PageHistoryItem[]>
    )

    return (
      <div className={cn('space-y-6', className)}>
        {Object.entries(groupedItems).map(([nodeType, items]) => (
          <div key={nodeType} className="space-y-3">
            <div className="flex items-center gap-2">
              <h3 className="text-sm font-medium text-muted-foreground">
                {getnodeTypeDisplayName(nodeType as HistorynodeType)}
              </h3>
              <span className="text-xs text-muted-foreground bg-muted px-2 py-1 rounded">
                {items.length} item{items.length !== 1 ? 's' : ''}
              </span>
            </div>
            <div className="space-y-3 ml-4">
              {items.map((item) => (
                <div
                  key={item.id}
                  className="flex items-center gap-3 p-3 rounded-lg border hover:bg-accent hover:text-accent-foreground cursor-pointer group"
                  onClick={() => navigate(item.path)}
                >
                  <div className="flex items-center gap-3 flex-1 min-w-0">
                    <SafeIcon
                      icon={item.icon}
                      className="h-5 w-5 text-muted-foreground flex-shrink-0"
                    />
                    <div className="flex-1 min-w-0">
                      <div className="font-medium text-sm truncate">{item.title}</div>
                      <div className="text-xs text-muted-foreground truncate">{item.path}</div>
                      <div className="text-xs text-muted-foreground">
                        {(() => {
                          const date = new Date(item.timestamp)
                          const now = new Date()
                          const diffInMinutes = Math.floor(
                            (now.getTime() - date.getTime()) / (1000 * 60)
                          )

                          if (diffInMinutes < 1) return 'Just now'
                          if (diffInMinutes < 60) return `${diffInMinutes}m ago`

                          const diffInHours = Math.floor(diffInMinutes / 60)
                          if (diffInHours < 24) return `${diffInHours}h ago`

                          const diffInDays = Math.floor(diffInHours / 24)
                          if (diffInDays < 7) return `${diffInDays}d ago`

                          return date.toLocaleDateString()
                        })()}
                      </div>
                    </div>
                  </div>
                  {onRemoveFromHistory && (
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={(e) => onRemoveFromHistory(e, item.id)}
                      className="h-auto p-1 text-muted-foreground hover:text-destructive opacity-0 group-hover:opacity-100 transition-opacity flex-shrink-0"
                      title="Remove from history"
                    >
                      <X className="h-4 w-4" />
                    </Button>
                  )}
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>
    )
  }

  return null
}

// History remove handler - needs to be defined before the config that uses it
const createHistoryRemoveHandler = (removeFromHistory: (id: string) => void) => {
  return (e: React.MouseEvent, id: string) => {
    e.stopPropagation()
    removeFromHistory(id)
  }
}

// Configuration for the history page
const historyPageConfig = {
  nodeName: 'page',
  nodeNamePlural: 'pages',
  title: 'User History',
  description: 'Browse and navigate through your recently visited pages',
  searchPlaceholder: 'Search history...',
  defaultTab: 'all',
  FilterComponent: HistoryFilters,
  defaultFilters: { nodeType: 'all' } as HistoryFilters,
  GroupingComponent: (props: {
    nodes?: PageHistoryItem[]
    tasks?: PageHistoryItem[]
    projects?: PageHistoryItem[]
    messages?: PageHistoryItem[]
    groupBy: HistoryGroupBy
    className?: string
    onRemoveFromHistory?: (e: React.MouseEvent, id: string) => void
  }) => {
    return (
      <HistoryResultsGrouping
        nodes={props.nodes}
        groupBy={props.groupBy}
        className={props.className}
        onRemoveFromHistory={props.onRemoveFromHistory}
      />
    )
  },
  defaultGroupBy: 'none' as HistoryGroupBy,
  GroupingSelectorComponent: HistoryGroupingSelector,
  dataPropName: 'nodes' as const,
  filterNodes: (
    nodes: PageHistoryItem[],
    _activeTab: string | undefined,
    filters: HistoryFilters,
    searchQuery: string,
    _user?: { id: string; role: string } | undefined
  ) => {
    let filteredNodes = nodes

    // Apply search filter if query exists
    if (searchQuery && searchQuery.trim() !== '') {
      const query = searchQuery.toLowerCase().trim()
      filteredNodes = filteredNodes.filter((node) => {
        // Search in title and path
        const titleMatch = node.title.toLowerCase().includes(query)
        const pathMatch = node.path.toLowerCase().includes(query)
        return titleMatch || pathMatch
      })
    }

    // Filter by node type if specified
    if (filters.nodeType && filters.nodeType !== 'all') {
      filteredNodes = filteredNodes.filter((node) => {
        const nodeType = extractnodeType(node.path)
        return nodeType === filters.nodeType
      })
    }

    return filteredNodes
  },
  emptyStateIcon: Clock,
  emptyStateTitle: 'No user history',
  emptyStateDescription: 'Your recently visited pages will appear here',
}

export default function HistoryPage() {
  const { history, clearHistory, removeFromHistory } = useHistory()
  const handleRemoveFromHistory = createHistoryRemoveHandler(removeFromHistory)

  // NodeListPage state
  const [searchQuery, setSearchQuery] = useState('')
  const [activeTab, setActiveTab] = useState('all')
  const [showFilters, setShowFilters] = useState(false)
  const [groupBy, setGroupBy] = useState<HistoryGroupBy>('none')
  const [filters, setFilters] = useState<HistoryFilters>({ nodeType: 'all' })

  const handleFiltersChange = (newFilters: Partial<HistoryFilters>) => {
    setFilters((prev) => ({ ...prev, ...newFilters }) as HistoryFilters)
  }

  // Create the config with the remove handler
  const configWithProps = {
    ...historyPageConfig,
    groupingComponentProps: {
      onRemoveFromHistory: handleRemoveFromHistory,
    },
  }

  return (
    <NodeListPage
      nodes={history}
      loading={false}
      searchQuery={searchQuery}
      onSearchChange={setSearchQuery}
      activeTab={activeTab}
      onTabChange={setActiveTab}
      showFilters={showFilters}
      onFiltersToggle={() => setShowFilters(!showFilters)}
      filters={filters}
      onFiltersChange={handleFiltersChange}
      groupBy={groupBy}
      onGroupByChange={setGroupBy}
      config={configWithProps}
      additionalActions={
        history.length > 0 ? (
          <Button
            variant="outline"
            size="sm"
            onClick={clearHistory}
            className="text-muted-foreground hover:text-destructive"
          >
            <Trash2 className="mr-2 h-4 w-4" />
            Clear all history
          </Button>
        ) : null
      }
    />
  )
}
